import Fuse from 'fuse.js'

// Curated static navigation entries. Each entry can ship a `keywords` array
// for synonyms / abbreviations (e.g. "history" → Reports). The entries are
// rendered as Algolia-shaped hits with __nav: true and __localOrigin: true.

export const STATIC_NAV_ENTRIES = [
  {
    id: 'nav-recent', label: 'Recent', path: '/recent', icon: 'history', keywords: ['home', 'history'],
  },
  {
    id: 'nav-library', label: 'Library', path: '/library', icon: 'library', keywords: ['my content', 'sets', 'questions', 'folders'],
  },
  {
    id: 'nav-reports', label: 'Reports', path: '/reports', icon: 'history-inverted', keywords: ['analytics', 'history'],
  },
  {
    id: 'nav-scoresheet', label: 'Scoresheet', path: '/scoresheet', icon: 'scoresheet', keywords: ['gradebook', 'grades'],
  },
  {
    id: 'nav-discover', label: 'Discover', path: '/discover', icon: 'discover', keywords: ['browse', 'public'],
  },
  {
    id: 'nav-trash', label: 'Trash', path: '/trash', icon: 'trash', keywords: ['deleted', 'archived'],
  },
  {
    id: 'nav-account', label: 'Account', path: '/account', icon: 'gear', keywords: ['settings', 'profile', 'billing'],
  },
  {
    id: 'nav-explorer', label: 'Explorer', path: '/explorer', icon: 'compass', keywords: ['browse'],
  },
]

// Exported so the internal search-tuning sandbox can display/reset the defaults.
export const NAVIGATION_FUSE_OPTIONS = {
  threshold: 0.2,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
  includeMatches: true,
  keys: [
    { name: 'label', weight: 2 },
    { name: 'keywords', weight: 1 },
  ],
}

/**
 * Build dynamic navigation entries from Redux collections — one per section
 * (Class — name). Packs are excluded; they appear in the Library search instead.
 *
 * @param {Object} input
 * @param {Array}  input.sections
 * @returns {Array<Object>}
 */
export function buildDynamicNavigationEntries({ sections }) {
  const entries = []
  if (Array.isArray(sections)) {
    sections.forEach((section) => {
      if (!section || !section.id) return
      entries.push({
        id: `nav-class-${section.id}`,
        label: section.name,
        color: section.color,
        path: `/classes/${section.id}`,
        icon: 'class',
        keywords: ['class', 'section', section.name],
      })
    })
  }
  return entries
}

/**
 * Build a Fuse navigation index over a precomputed list of entries.
 * Caller is expected to memoise the entries array on its end so this
 * function is a thin allocator.
 */
export function buildNavigationFuseIndex(entries) {
  return new Fuse(Array.isArray(entries) ? entries : [], NAVIGATION_FUSE_OPTIONS)
}

/**
 * Run a query against a Fuse navigation index and return Algolia-shaped
 * flat hits, each carrying __nav: true, __localOrigin: true, plus the
 * original entry fields (label, path, icon, keywords). The Fuse score and
 * per-key match metadata are passed through as __localScore and
 * __localMatches for devtools / searchReporter inspection.
 */
export function searchNavigation(fuse, query, limit = 8) {
  if (!query || !fuse) return []
  return fuse.search(query, { limit }).map(({ item, score, matches }) => ({
    ...item,
    objectID: item.id,
    __nav: true,
    __localOrigin: true,
    __localScore: score,
    __localMatches: matches,
  }))
}
